import logoW from "@assets/W_1771840384312.webp";

/**
 * Wrkt 2026 hero background — single source of truth.
 * Layers (bottom→top):
 *  1. Aurora green gradient (--grad-green-hero)
 *  2. Pulsating red↔mint glow (kept from original brand)
 *  3. Exactly ONE solid W watermark (very subtle, drifts left)
 *  4. Exactly ONE line-art W (single contour, drifts right)
 *  5. Bottom fade to page background for clean handoff
 *
 * Used as the first child of any hero <section>. Section must be
 * `relative overflow-hidden`. Content goes after it with `z-10 relative`.
 */
export function HeroBackground() {
  return (
    <>
      <div className="absolute inset-0 z-0 bg-grad-green-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0A1F14]"></div>
        <style>{`
          @keyframes wrkt-glow-pulse {
            0%   { background-color: rgba(141, 24, 43, 0.5); transform: scale(1);    opacity: 0.5; }
            50%  { background-color: rgba(76, 234, 172, 0.2); transform: scale(1.04); opacity: 0.35; }
            100% { background-color: rgba(141, 24, 43, 0.5); transform: scale(1);    opacity: 0.5; }
          }
          @keyframes wrkt-w-solid {
            0%, 100% { transform: translate(-52%, -50%) scale(1);     opacity: 0.05; }
            50%      { transform: translate(-48%, -50%) scale(1.015); opacity: 0.07; }
          }
          @keyframes wrkt-w-line {
            0%, 100% { transform: translate(-48%, -50%) scale(1);    opacity: 0.14; }
            50%      { transform: translate(-52%, -50%) scale(1.01); opacity: 0.20; }
          }
          .wrkt-hero-glow { animation: wrkt-glow-pulse 20s ease-in-out infinite; }
          .wrkt-hero-w-solid { animation: wrkt-w-solid 28s ease-in-out infinite; }
          .wrkt-hero-w-line { animation: wrkt-w-line 28s ease-in-out infinite; }
          /* Mobiel: geen doorlopende animaties op grote blur/blend-lagen.
             Voorkomt compositor-flikker (foto's die kort wegvallen bij scrollen). */
          @media (max-width: 767px), (prefers-reduced-motion: reduce) {
            .wrkt-hero-glow, .wrkt-hero-w-solid, .wrkt-hero-w-line { animation: none; }
          }
        `}</style>
        <div
          className="absolute top-1/4 -left-1/4 w-[600px] h-[600px] md:w-[800px] md:h-[800px] wrkt-hero-glow rounded-full blur-[100px] md:blur-[120px] pointer-events-none mix-blend-screen"
          style={{ backgroundColor: "rgba(141, 24, 43, 0.5)", opacity: 0.5 }}
        ></div>
      </div>

      {/* Solid W — drifts left */}
      <div
        className="wrkt-hero-w-solid absolute top-1/2 left-1/2 w-full max-w-4xl pointer-events-none z-0 mix-blend-screen"
        style={{ transform: "translate(-50%, -50%)", opacity: 0.06 }}
        aria-hidden="true"
      >
        <img src={logoW} alt="" width={855} height={685} decoding="async" fetchPriority="high" className="w-full h-auto" />
      </div>

      {/* Line W — single contour, drifts right */}
      <div
        className="wrkt-hero-w-line absolute top-1/2 left-1/2 w-full max-w-5xl pointer-events-none z-0"
        style={{ transform: "translate(-50%, -50%)", opacity: 0.17 }}
        aria-hidden="true"
      >
        <svg viewBox="0 0 855 685" className="w-full h-auto" fill="none">
          {/* Brand W = two V's whose legs are at the same 18.3° from vertical.
              Each V's inner peak crosses the other's at the top — the LEFT V's
              inner peak (516,0) sits to the RIGHT of the RIGHT V's inner peak
              (339,0). All four legs traced from the actual logoW watermark;
              the V tips fall below y=685 so the strokes end at the bottom edge,
              matching exactly how the solid watermark crops. */}
          <g stroke="#5CE698" strokeWidth="1.25" strokeLinejoin="miter" strokeLinecap="square">
            <path d="M 14 150 L 191 685" />
            <path d="M 516 0 L 295 685" />
            <path d="M 339 0 L 560 685" />
            <path d="M 841 150 L 665 685" />
          </g>
        </svg>
      </div>
    </>
  );
}
